import { useState, useCallback, useEffect } from 'react';
import dataService from '../services/dataService';
import authService from '../services/authService';
import { API_CONFIG } from '../config/api';

/**
 * Hook para buscar marcas e modelos de veículos
 * @param {Object} initialParams - Parâmetros iniciais
 * @returns {Object} - Estados e funções para busca de veículos
 */
export const useVehicleBrands = (initialParams = {}) => {
  const [brandsAndModels, setBrandsAndModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchVehicleBrands = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Iniciando busca de veículos...');

      const result = await dataService.getVehicleBrandsAndModels({ 
        ...initialParams, 
        ...params 
      });

      if (result.success) {
        setBrandsAndModels(result.data);
        console.log('✅ Veículos carregados:', result.data.length, 'itens');
        if (result.fallback) {
          console.log('⚠️ Usando dados locais:', result.message);
        }
      } else {
        setError(result.message);
        setBrandsAndModels([]);
      }

      return result;
    } catch (err) {
      const errorMessage = 'Erro ao buscar veículos';
      setError(errorMessage);
      setBrandsAndModels([]);
      console.error('❌ Erro no hook useVehicleBrands:', err);
      
      return {
        success: false,
        data: [],
        message: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [initialParams]);

  const searchVehicles = useCallback(async (searchTerm) => {
    return await fetchVehicleBrands({ search: searchTerm });
  }, [fetchVehicleBrands]);

  const clearVehicles = useCallback(() => {
    setBrandsAndModels([]);
    setError(null);
  }, []);

  const getVehiclesByBrand = useCallback((brandName) => {
    return brandsAndModels.find(brand => 
      brand.name.toLowerCase() === brandName.toLowerCase()
    );
  }, [brandsAndModels]);

  const getVehiclesBySearch = useCallback((searchTerm) => {
    if (!searchTerm) return brandsAndModels;

    return brandsAndModels.filter(brand =>
      brand.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      brand.models.some(model => 
        model.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    ).map(brand => ({
      ...brand,
      models: brand.models.filter(model =>
        model.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }));
  }, [brandsAndModels]);

  // Busca inicial se há parâmetros
  useEffect(() => {
    if (initialParams.autoLoad !== false) {
      fetchVehicleBrands();
    }
  }, []); // Executa apenas uma vez na montagem

  return {
    brandsAndModels,
    loading,
    error,
    fetchVehicleBrands,
    searchVehicles,
    clearVehicles,
    getVehiclesByBrand,
    getVehiclesBySearch,
    hasVehicles: brandsAndModels.length > 0,
    hasError: !!error
  };
};

export default useVehicleBrands;
